import * as React from 'react';
import Modal from 'react-bootstrap/Modal';
import axios from 'axios';
import { toast } from 'react-toastify';
import authService from './api-authorization/AuthorizeService';
import * as DateHelpers from './DateHelpers';
import GooglePlacesDropdown from './GooglePlaces';
import { AddressDetails } from './models/JobInterfaces';

interface AllocateScheduleJobProps {
    scheduleItemDetail: ScheduleItemDetail;
    customerNewFuelInvoice: boolean;
    showModal: boolean;
    hideModal(): void;
    saveModal(): void;
}

interface ScheduleItemDetail {
    jobId: number;
    jobQuoteItemId: number;
    item: string;
    itemTypeId: number;
    installOnly: boolean;
    isEvent: boolean;
    needsPickup: boolean;
    hirePeriod: number;
    frequencyId: number;
    customerAccountTypeId: number;
    customerLinkedToReckon: boolean;
    siteContact: string;
    jobOnHold: boolean;
}

interface DeliveryAddress {
    deliveryAddress: string;
    deliveryAddressGooglePlaceId: string;
    deliveryAddressLatitude: number;
    deliveryAddressLongitude: number;
    pickupId: number;
};

const AllocateScheduleJobPopup = (props: AllocateScheduleJobProps) => {
    var schDate = DateHelpers.addDaysToCurrentDate(1);

    const [scheduleDate, setScheduleDate] = React.useState(schDate);
    const [onHireDate, setOnHireDate] = React.useState(schDate);
    const [pickupDate, setPickupDate] = React.useState("");
    const [offHireDate, setOffHireDate] = React.useState("");
    const [purchaseOrderNumber, setPurchaseOrderNumber] = React.useState("");
    const [purchaseOrderNumberFuel, setPurchaseOrderNumberFuel] = React.useState("");
    const [paymentType, setPaymentType] = React.useState(-1);
    const [schedulePickup, setSchedulePickup] = React.useState(false);
    const [errors, setErrors] = React.useState<{ [key: string]: string }>({});
    const [saveDisabled, setSaveDisabled] = React.useState(false);
    const [googleAddress, setGoogleAddress] = React.useState<DeliveryAddress>({
        deliveryAddress: "",
        deliveryAddressGooglePlaceId: "",
        deliveryAddressLatitude: 0,
        deliveryAddressLongitude: 0,
        pickupId: 0
    });

    React.useEffect(() => {
        if (props.scheduleItemDetail.jobQuoteItemId > 0) {
            var defaultPayType = props.scheduleItemDetail.customerAccountTypeId === 1 ? -1 : props.scheduleItemDetail.customerAccountTypeId == 2 ? 1 : 2;
            setPaymentType(defaultPayType);
            setSchedulePickup(props.scheduleItemDetail.needsPickup);
            if (props.scheduleItemDetail.needsPickup && !props.scheduleItemDetail.installOnly) {
                let endDate = DateHelpers.addDaysToDate(onHireDate, props.scheduleItemDetail.hirePeriod - 1);
                setOffHireDate(endDate);
            }
        }
    }, [props.scheduleItemDetail.jobQuoteItemId]);

    const handleOnHireDateChange = (e: any) => {
        clearError(e);

        setOnHireDate(e.target.value);
        let isValidDate = DateHelpers.isValidDate(e.target.value);
        if (offHireDate && isValidDate) {
            let endDate = DateHelpers.addDaysToDate(e.target.value, props.scheduleItemDetail.hirePeriod - 1);
            setOffHireDate(endDate);
        }
    }

    const handleRadioChange = (e: any) => {
        clearError(e);
        let value = e.target.value * 1;
        setPaymentType(value);
    }

    const handleSchedulePickup = (e: any) => {
        let schPickup = e.target.checked;
        setSchedulePickup(schPickup);
        if (schPickup) {
            //calculate offhire date using hire period and scheudle date
            let isValidDate = DateHelpers.isValidDate(scheduleDate);
            if (isValidDate) {
                let endDate = DateHelpers.addDaysToDate(scheduleDate, props.scheduleItemDetail.hirePeriod - 1);
                setOffHireDate(endDate);
            }
        } else {
            //clear offhire and pickup dates
            setOffHireDate("");
            setPickupDate("");
        }
    }

    //update delivery address from google
    const setAddress = async (addressDetails: AddressDetails) => {
        setGoogleAddress(prevState => ({
            ...prevState,
            deliveryAddress: addressDetails.fullAddress,
            deliveryAddressGooglePlaceId: addressDetails.placeId,
            deliveryAddressLatitude: addressDetails.latitude,
            deliveryAddressLongitude: addressDetails.longitude
        }));
    }

    const clearError = (e: any) => {
        var error = errors;
        if (e.target.value) {
            delete error[e.target.name];
            setErrors(error);
        }
    }

    const hideModal = () => {
        resetForm();
        props.hideModal();
    }

    const resetForm = () => {
        setScheduleDate("");
        setOnHireDate("");
        setPickupDate("");
        setOffHireDate("");
        setPurchaseOrderNumber("");
        setPurchaseOrderNumberFuel("");
        setPaymentType(-1);
        setSchedulePickup(false);
        setErrors({});
        setSaveDisabled(false);
        setGoogleAddress({
            deliveryAddress: "",
            deliveryAddressGooglePlaceId: "",
            deliveryAddressLatitude: 0,
            deliveryAddressLongitude: 0,
            pickupId: 0
        });
    }

    const validate = () => {
        let error: any = {};
        let formIsValid = true;

        if (!scheduleDate) {
            formIsValid = false;
            error["scheduleDate"] = "Schedule Date is required";
        } else if (!DateHelpers.isValidDate(scheduleDate)) {
            formIsValid = false;
            error["scheduleDate"] = "Valid Schedule Date is required";
        }

        if (!onHireDate) {
            formIsValid = false;
            error["onHireDate"] = "On Hire Date is required";
        } else if (!DateHelpers.isValidDate(onHireDate)) {
            formIsValid = false;
            error["onHireDate"] = "Valid On Hire Date is required";
        } else if (!DateHelpers.compareDate(scheduleDate, onHireDate)) {
            formIsValid = false;
            error["onHireDate"] = "On Hire Date >= Schedule Date";
        }

        if ((props.scheduleItemDetail.needsPickup && !props.scheduleItemDetail.installOnly) || pickupDate || offHireDate) {
            if (!pickupDate) {
                formIsValid = false;
                error["pickupDate"] = "Pickup Date is required";
            } else if (!DateHelpers.isValidDate(pickupDate)) {
                formIsValid = false;
                error["pickupDate"] = "Valid Pickup Date is required";
            } else if (!DateHelpers.compareDate(onHireDate, pickupDate)) {
                formIsValid = false;
                error["offHireDate"] = "Pickup Date >= On Hire Date";
            }

            if (!offHireDate) {
                formIsValid = false;
                error["offHireDate"] = "Off Hire Date is required";
            } else if (!DateHelpers.isValidDate(offHireDate)) {
                formIsValid = false;
                error["offHireDate"] = "Valid Off Hire Date is required";
            } else if (!DateHelpers.compareDate(offHireDate, pickupDate)) {
                formIsValid = false;
                error["offHireDate"] = "Offhire Date <= Pickup Date";
            }
        }

        if (props.scheduleItemDetail.customerAccountTypeId === 3 && !purchaseOrderNumber) {
            formIsValid = false;
            error["purchaseOrderNumber"] = "PO Number is required";
        }

        if (props.scheduleItemDetail.customerAccountTypeId === 1 && paymentType < 1 && !props.scheduleItemDetail.jobOnHold) {
            formIsValid = false;
            error["paymentType"] = "Payment Type is required";
        }

        setErrors(error);

        return formIsValid;
    }

    const scheduleJob = () => {
        if (validate()) {
            if (saveDisabled) {
                return;
            }
            setSaveDisabled(true);
            saveScheduleJob();
        } else {
            toast.error("Fix errors on screen before scheduling!");
        }
    }  

    const saveScheduleJob = async () => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var scheduleJob = {
            jobId: props.scheduleItemDetail.jobId,
            itemTypeId: props.scheduleItemDetail.itemTypeId,
            jobQuoteItemId: props.scheduleItemDetail.jobQuoteItemId,
            scheduleDate: scheduleDate,
            onHireDate: onHireDate,
            pickupDate: pickupDate,
            offHireDate: offHireDate,
            purchaseOrderNumber: purchaseOrderNumber,
            purchaseOrderNumberFuel: purchaseOrderNumberFuel,
            siteContact: props.scheduleItemDetail.siteContact,
            paymentType: paymentType,
            frequencyId: props.scheduleItemDetail.frequencyId,
            deliveryAddress: googleAddress.deliveryAddress,
            deliveryAddressGooglePlaceId: googleAddress.deliveryAddressGooglePlaceId,
            deliveryAddressLatitude: googleAddress.deliveryAddressLatitude,
            deliveryAddressLongitude: googleAddress.deliveryAddressLongitude,
            subId: user.sub
        };

        //schedule job
        axios.post('api/Job/ScheduleOnHireJob', scheduleJob, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            resetForm();
            toast.success("Job scheduled successfully");
            props.saveModal();
        })
        .catch(error => {
            setSaveDisabled(false);
            toast.error(error.message);
        });
    }

    const prepaidNote = "PREPAID CUSTOMER => REMEMBER TO CHARGE CREDIT CARD";

    return (
        <Modal size="lg" show={props.showModal} onHide={hideModal}>
            <Modal.Header className="modal__header--colour">
                <Modal.Title>Schedule {props.scheduleItemDetail.item}</Modal.Title>
                <button type="button" className="close modal__close--colour" onClick={hideModal}>&times;</button>
            </Modal.Header>
            <Modal.Body>
                <div className={!props.scheduleItemDetail.customerLinkedToReckon ? "makeitred makeitbold marginLeftLabel" : "hidden"}>Let Accounts know that the customer needs to be linked to Reckon.</div>
                <label className="input-group" htmlFor="scheduleDate">
                    <span className="label">Schedule Date</span>
                    <input type='date' id="scheduleDate" name="scheduleDate" className='input' value={scheduleDate} onChange={(e) => { clearError(e); setScheduleDate(e.target.value) } }></input>
                </label>
                <span className={errors["scheduleDate"] ? "label errors marginLeftLabel" : "hidden"}>{errors["scheduleDate"]}</span>

                <label className="input-group" htmlFor="onHireDate">
                    <span className="label">On Hire Date</span>
                    <input type='date' id="onHireDate" name="onHireDate" className='input' value={onHireDate} onChange={(e) => handleOnHireDateChange(e) }></input>
                </label>
                <span className={errors["onHireDate"] ? "label errors marginLeftLabel" : "hidden"}>{errors["onHireDate"]}</span>

                <label className={props.scheduleItemDetail.installOnly ? "hidden" : "input-group"} htmlFor="schedulePickup">
                    <span className="label label--setsizesm">Schedule Pickup?</span>
                    <input className="checkbox" type="checkbox" id="schedulePickup" name="schedulePickup" checked={schedulePickup} onChange={(e) => handleSchedulePickup(e)} disabled={props.scheduleItemDetail.needsPickup}></input>
                </label>

                <div className={(props.scheduleItemDetail.needsPickup || schedulePickup) && (!props.scheduleItemDetail.installOnly || props.scheduleItemDetail.itemTypeId === 35) ? "" : "hidden"}>
                    <label className="input-group" htmlFor="offHireDate">
                        <span className="label">Off Hire Date</span>
                        <input type='date' id="offHireDate" name="offHireDate" className='input' value={offHireDate} onChange={(e) => { clearError(e); setOffHireDate(e.target.value); }}></input>
                    </label>
                    <span className={errors["offHireDate"] ? "label errors marginLeftLabel" : "hidden"}>{errors["offHireDate"]}</span>

                    <label className="input-group" htmlFor="pickupDate">
                        <span className="label">Pickup Date</span>
                        <input type='date' id="pickupDate" name="pickupDate" className='input' value={pickupDate} onChange={(e) => { clearError(e); setPickupDate(e.target.value); }}></input>
                    </label>
                    <span className={errors["pickupDate"] ? "label errors marginLeftLabel" : "hidden"}>{errors["pickupDate"]}</span>
                </div>

                <label className={props.scheduleItemDetail.customerAccountTypeId === 3 ? "input-group" : "hidden"} htmlFor="purchaseOrderNumber">
                    <span className="label">PO Number</span>
                    <input className="input" type="text" id="purchaseOrderNumber" name="purchaseOrderNumber" value={purchaseOrderNumber} onChange={(e) => { clearError(e); setPurchaseOrderNumber(e.target.value); } }></input>
                </label>
                <span className={errors["purchaseOrderNumber"] ? "label errors marginLeftLabel" : "hidden"}>{errors["purchaseOrderNumber"]}</span>

                <label className={props.customerNewFuelInvoice && props.scheduleItemDetail.customerAccountTypeId === 3 ? "input-group" : "hidden"} htmlFor="purchaseOrderNumberFuel">
                    <span className="label">PO Number Fuel Surcharge</span>
                    <input className="input" type="text" id="purchaseOrderNumberFuel" name="purchaseOrderNumberFuel" value={purchaseOrderNumberFuel} onChange={(e) => { clearError(e); setPurchaseOrderNumberFuel(e.target.value); }}></input>
                </label>

                <div className={props.scheduleItemDetail.isEvent ? "" : "hidden"}>
                    <div>Set delivery address ONLY IF DIFFERENT from Site Address</div>
                    <GooglePlacesDropdown addressId="autocomplete" addressLabel={"Deivery Address"} placeholder="" className="input-group" useLargeLabel={false} afterValid={setAddress} />
                </div>

                <div className={props.scheduleItemDetail.customerAccountTypeId === 1 && !props.scheduleItemDetail.jobOnHold ? "" : "hidden"}>
                    <div>
                        <label className="input-group" htmlFor="payType">
                            <span className="label label--inline">Payment Type</span>
                            <input type="radio" className="marginRight10" name="paymentType" id="creditcard" value={3} checked={paymentType === 3} onChange={(e) => handleRadioChange(e)}></input>
                            <label htmlFor="creditcard" className="marginRight10 marginBottomNone">Credit Card</label>
                            <input type="radio" className="marginRight10" name="paymentType" id="directdebit" value={4} checked={paymentType === 4} onChange={(e) => handleRadioChange(e)}></input>
                            <label htmlFor="directdebit" className="marginRight10 marginBottomNone">Direct Debit</label>
                        </label>
                        <span className={errors["paymentType"] ? "label errors marginLeftLabel" : "hidden"}>{errors["paymentType"]}</span>
                    </div>

                    <div className={paymentType < 0 || paymentType === 3 ? "newJobPricelist--prepaid" : "hidden"}>{prepaidNote}</div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <button className="popup-button--yes" onClick={scheduleJob}>Schedule</button>
                
            </Modal.Footer>
        </Modal>
    )
}

export default AllocateScheduleJobPopup;